import { AnimalProgressValue } from 'src/app/api/models';
import { RgTextList } from 'src/app/medical-history/models/rg-text-list.model';
import { DateTimeHelper } from './datetime.helper';
import { SummaryStatusType } from 'src/app/medical-history/enums/summary-status-type.enum';

export class MedicalHistoryHelper {

  static checkForTextReplacement(text?: string): boolean {
    return text && text.includes('#T#') ? true : false;
  }

  static roundingToNearest05(value: number): number {
    return Number(this.roundingToNearest05String(value));
  }

  static roundingToNearest05String(value: number | undefined): string {
    if (value) {
      return (Math.round(value * 20) / 20).toFixed(2);
    }
    return '';
  }

  static setAmountBelowO5to05(amount: string | undefined | number): number | undefined {
    if (amount !== undefined && amount !== 0) {
      return Number(Number(amount).toFixed(2)) < 0.5 ? 0.5 : Number(Number(amount).toFixed(2));
    }
    return amount
  }

  static roundToTwoDecimals(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  static getIndexOfClosestMeasure(
    animalData: Array<AnimalProgressValue>,
    currentVisitDate: string | Date
  ): number {
    let visitDateTime = DateTimeHelper.copyDate(currentVisitDate).getTime();
    let currentDifference: number | undefined = undefined;
    let visitMeasureIndex = -1;
    animalData.map((measure, index) => {
      if (measure.visitDate) {
        const measureTimeValue = DateTimeHelper.copyDate(
          measure.visitDate
        ).getTime();
        if (measureTimeValue <= visitDateTime) {
          const different = visitDateTime - measureTimeValue;
          if (currentDifference === undefined) {
            currentDifference = different;
            visitMeasureIndex = index;
          } else if (currentDifference && different < currentDifference) {
            currentDifference = different;
            visitMeasureIndex = index;
          }
        }
      }
    });
    return visitMeasureIndex;
  }

  static getDiscountFactor(
    posAmount: number,
    discountSellingPrice: number
  ): number {
    if (posAmount && discountSellingPrice) {
      return Number(((posAmount * 100) / discountSellingPrice).toFixed(2));
    }
    return 0;
  }

  static getDiscountAmount(
    posAmountFactor: number,
    discountSellingPrice: number
  ): string {
    if (posAmountFactor && discountSellingPrice) {
      const factoPercentage = posAmountFactor / 100;
      return MedicalHistoryHelper.roundingToNearest05String(
        discountSellingPrice * factoPercentage
      );
    }
    return '0';
  }

  /**
   * Remove the code in front of the diagnois text, ex remove (2.1 ). The code was added on loading diagnosis
   * @param prescriptionDesignation diagnosisText
   * @returns the diagnois text without the code in the begining
   */
  static removeCodeFromPrescriptionText(
    prescriptionDesignation: string | undefined
  ): string {
    let textWithoutCode = '';
    if (prescriptionDesignation) {
      const textSplit = prescriptionDesignation.split(' ');
      if (textSplit.length > 0) {
        // removes the code text and the .length+1 its to also remove the space
        textWithoutCode = prescriptionDesignation.substring(
          textSplit[0].length + 1
        );
      }
    }
    return textWithoutCode;
  }

  static getVisitDateTime(date: Date, time: Date): Date {
    const visitAt: Date = date;
    if (time) {
      visitAt.setHours(time.getHours());
      visitAt.setMinutes(time.getMinutes());
      // to force allways 0 in seconds, date may have seconds
      visitAt.setSeconds(0);
    }
    return visitAt;
  }

  static getTotalDosis(
    amount: number,
    durationInDaysSelected: number,
    servingModeValue: number | undefined,
    accumulator: number
  ): number {
    if (amount && servingModeValue && durationInDaysSelected) {
      const durationInDays = Number(durationInDaysSelected);
      const menge = Number(amount);
      const adminModeValue = Number(servingModeValue);
      return (
        accumulator +
        (Math.floor((durationInDays - 1) * adminModeValue + 0.01) +
          Math.ceil(1 * adminModeValue)) *
        menge *
        1
      );
    }
    return accumulator;
  }

  static convertHexQuantity(
    quantity: number,
    salesArticleId: string,
    articleId: string,
    serviceArticleTreatments: RgTextList[],
    toHexUnits: boolean = true
  ): number {
    let ratioTreatmentValue = 0;
    const treatmentDefaultData = serviceArticleTreatments.find(
      serviceArticleTreatment =>
        serviceArticleTreatment.salesArticle?.id === salesArticleId
    );
    // sort the treatments that have the same articleId by the smallest ratio
    const treatmentArticles = serviceArticleTreatments
      .filter(
        serviceArticleTreatment =>
          serviceArticleTreatment.salesArticle?.articleId === articleId
      )
      .sort((a, b) => {
        if (a && b) {
          const ratioA = Number(
            a.salesArticle?.rationSoldToSuppliedArticle?.split('/')[1]
          );
          const ratioB = Number(
            b.salesArticle?.rationSoldToSuppliedArticle?.split('/')[1]
          );
          return ratioB - ratioA;
        }
        return -1;
      });
    if (treatmentDefaultData) {
      const ratioValues = treatmentDefaultData.salesArticle?.rationSoldToSuppliedArticle?.split(
        '/'
      );
      if (ratioValues && ratioValues.length > 0 && quantity !== undefined) {
        const ratioValueSmallest = treatmentArticles[0].salesArticle?.rationSoldToSuppliedArticle?.split(
          '/'
        );
        // if the treatment with smallest ratio is the same has the treatment selected by the user, it doesn't need to convert
        if (ratioValueSmallest && ratioValueSmallest[1] !== ratioValues[1]) {
          if (toHexUnits) {
            ratioTreatmentValue =
              (quantity * Number(ratioValues[0])) / Number(ratioValues[1]);
            ratioTreatmentValue =
              ratioTreatmentValue * Number(ratioValueSmallest[1]);
          } else {
            ratioTreatmentValue = quantity / Number(ratioValueSmallest[1]);
            ratioTreatmentValue =
              (ratioTreatmentValue * Number(ratioValues[1])) /
              Number(ratioValues[0]);
          }
        } else {
          ratioTreatmentValue = quantity;
        }
      }
    }
    return ratioTreatmentValue;
  }

  static getNumber(value: string): any {
    return !isNaN(parseFloat(value || '')) ? Number(value) : null;
  }

  static calculateTreatmentAmount(paymentStatus: string | undefined, previousQuantity: number | undefined, currentQuantity: number | undefined, currentAmount: string ,unitSalesPrice: number): number | undefined{
    let amount = undefined;
    if (paymentStatus && (paymentStatus === SummaryStatusType.DISCOUNTED_PURCHASE_PRICE || paymentStatus === SummaryStatusType.DISCOUNTED_SELLING_PRICE || paymentStatus === SummaryStatusType.MANUAL_DISCOUNT)) {
      amount = Number(currentAmount) / Number(previousQuantity || 1);
    } else {
      amount = unitSalesPrice;
    }
    if (amount) {
      const amountQuantity = (Number(amount) * (currentQuantity || 1));
      const amountFinal = (paymentStatus !== SummaryStatusType.MANUAL_DISCOUNT) ? MedicalHistoryHelper.setAmountBelowO5to05(amountQuantity) : amountQuantity;
      return amountFinal
    }

    return undefined;
  }
}
